<template>
    <div class="domain-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="header-text">
                    <h3>{{ widgetTitle }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading" class="loading-screen">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div v-if="!isRevalidateMode" class="stepper-container">
                        <app-stepper
                            :steps="3"
                            :current-step="currentStep"
                            :is-complete="isProcessComplete"
                        />
                    </div>
                    <div class="steps-container">
                        <domain-widget-step1
                            v-if="currentStep === 1"
                            ref="1"
                            :form="form"
                        />
                        <domain-widget-step2
                            v-else-if="currentStep === 2"
                            ref="2"
                            :form="form"
                            :is-custom="isCustom"
                            :form-errors="formErrors"
                            :v="$v"
                        />
                        <domain-widget-step3
                            v-else
                            ref="3"
                            :form="form"
                            :is-custom="isCustom"
                            :is-revalidate-mode="isRevalidateMode"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        class="cancel-btn transparent-btn"
                        size="normal"
                        @click="closeWidget"
                    >
                        {{ isLastStep ? 'Close' : 'Cancel' }}
                    </app-button>
                </div>
                <div v-if="!loading" class="action-container">
                    <app-button v-if="currentStep === 2" class="back-btn" @click="goBack"
                        >Back</app-button
                    >
                    <app-button
                        v-if="isLastStep && isCustom"
                        class="next-btn"
                        @click="validate"
                        >{{ validateLabel }}</app-button
                    >
                    <app-button
                        v-if="!isLastStep"
                        :disabled="isNextDisabled"
                        class="next-btn"
                        @click="goNext"
                        >Next</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import { DomainService } from '@/scripts/services/domain/domain-service';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const DomainWidgetStep1 = () =>
    import('@/components/sliding-widgets/domain-widget/domain-widget-step1.vue');
const DomainWidgetStep2 = () =>
    import('@/components/sliding-widgets/domain-widget/domain-widget-step2.vue');
const DomainWidgetStep3 = () =>
    import('@/components/sliding-widgets/domain-widget/domain-widget-step3.vue');
const AppStepper = () => import('@/components/common/app-stepper.vue');

export default {
    name: 'domain-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'domain-widget-step1': DomainWidgetStep1,
        'domain-widget-step2': DomainWidgetStep2,
        'domain-widget-step3': DomainWidgetStep3,
        'app-stepper': AppStepper,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            currentStep: 1,
            steps: {
                1: {
                    label: 'Step 1',
                    actions: {
                        nextAction: {
                            label: 'Next',
                            callback: () => {
                                this.getFormDataFromCurrentStep().then(() => {
                                    this.goToStep(2);
                                });
                            },
                        },
                    },
                },

                2: {
                    label: 'Step 2',
                    actions: {
                        backAction: {
                            label: 'Back',
                            callback: () => {
                                this.getFormDataFromCurrentStep().then(() => {
                                    this.restoreInputValues();
                                    this.goToStep(1);
                                });
                            },
                        },

                        nextAction: {
                            label: 'Next',
                            callback: () => {
                                this.getFormDataFromCurrentStep().then(() => {
                                    this.createDomain();
                                });
                            },
                        },
                    },
                },

                3: {
                    label: 'Step 3',
                    actions: {
                        validateAction: {
                            label: 'Validate',
                            callback: () => {
                                this.getValidation();
                            },
                        },
                    },
                },
            },

            form: {
                domainType: { name: 'GlassHive Domain', value: 'Glasshive' },
                domainName: null,
                subdomain: null,
                brandDomain: null,
                topLevelDomain: null,
                isCustom: false,
                isValid: null,
                isValidated: false,
            },

            formErrors: {
                domainUrl: false,
            },

            currentDomainId: null,
        };
    },

    validations() {
        if (this.isCustom) {
            return {
                form: {
                    brandDomain: {
                        required,
                    },

                    subdomain: {
                        required,
                    },

                    topLevelDomain: {
                        required,
                    },
                },
            };
        } else {
            return {
                form: {
                    subdomain: {
                        required,
                    },
                },
            };
        }
    },

    computed: {
        isRevalidateMode() {
            return this.widget.domainId !== null && this.widget.domainId !== undefined;
        },

        isCustom() {
            return this.form.isCustom;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        isLastStep() {
            return this.currentStep === 3;
        },

        isProcessComplete() {
            return this.isCustom
                ? this.form.isValidated && this.form.isValid
                : this.currentStep === 3;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        isNextDisabled() {
            return this.currentStep === 2 && !this.isFormValid;
        },

        validate() {
            return this.steps[this.currentStep].actions.validateAction.callback;
        },

        validateLabel() {
            return this.steps[this.currentStep].actions.validateAction.label;
        },

        widgetTitle() {
            if (this.isRevalidateMode) {
                return 'Domain Validation Status';
            }
            return this.currentStep !== 1
                ? `Add New Domain - ${this.form.domainType.name}`
                : 'Add New Domain';
        },

        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    created() {
        if (this.isRevalidateMode) {
            this.getRevalidateDomainData();
        }
    },

    methods: {
        setValidateLabel() {
            this.steps[this.currentStep].actions.validateAction.label = this.form
                .isValidated
                ? 'Revalidate'
                : 'Validate';
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.currentStep = step;
        },

        restoreInputValues() {
            this.form = {
                ...this.form,
                subdomain: null,
                brandDomain: null,
                topLevelDomain: null,
            };
            this.formErrors.domainUrl = false;
        },

        getFormDataFromCurrentStep() {
            return new Promise((resolve) => {
                this.$refs[this.currentStep].next().then((stepFormData) => {
                    this.form = { ...this.form, ...stepFormData };
                    resolve();
                });
            });
        },

        submitDomainForm() {
            return new Promise((resolve, reject) => {
                const request = {
                    DomainName: this.form.domainName,
                    SubDomain: this.form.subdomain,
                    BrandDomain: this.form.brandDomain,
                    TopLevelDomain: this.form.topLevelDomain,
                    IsCustom: this.form.isCustom,
                    AccountId: this.accountId,
                };

                DomainService.post(request)
                    .then(({ data }) => {
                        this.currentDomainId = data;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        createDomain() {
            this.submitDomainForm()
                .then(() => {
                    this.$helpers.showSuccessNotification('Domain created successfully.');
                    this.goToStep(3);
                })
                .catch((error) => {
                    if (error.response.status === 400) {
                        this.formErrors.domainUrl = true;
                    }
                    this.$helpers.showFailNotification(
                        'An error occurred trying to create the domain.',
                        { error }
                    );
                });
        },

        getValidation() {
            this.loading = true;

            const id = !this.widget.domainId ? this.currentDomainId : this.widget.domainId;

            DomainService.validate(id)
                .then(({ data }) => {
                    this.form.isValid = data;
                    this.form.isValidated = true;
                    this.setValidateLabel();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to validate domain.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getRevalidateDomainData() {
            this.loading = true;
            this.currentStep = 3;
            const { domainId } = this.widget;
            DomainService.get(domainId)
                .then(({ data }) => {
                    this.setRevalidateDomainInformation(data.value[0]);
                    this.setValidateLabel();
                    this.loading = false;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get domain info.', { error });
                });
        },

        setRevalidateDomainInformation(data) {
            this.form = {
                ...this.form,
                domainName: data.DomainName,
                subdomain: data.SubDomain,
                brandDomain: data.BrandDomain,
                topLevelDomain: data.TopLevelDomain,
                isCustom: data.IsCustom,
                isValid: data.Validated,
                isValidated: true,
            };
        },
    },
};
</script>

<style>
.domain-widget .widget-contents-container .content-wrapper {
    padding: 22px 17px 22px 31px;
}

.domain-widget .stepper-container {
    width: 190px;
    margin-bottom: 55px;
}

.domain-widget .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.domain-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.domain-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: 2px solid #cac6cb;
    width: 120px;
}

.domain-widget .action-container {
    display: flex;
    column-gap: 13px;
}

.domain-widget .action-container .btn {
    width: 100px;
}

.domain-widget .action-container .back-btn:hover {
    background: #dbedfa;
}

.domain-widget .action-container .back-btn {
    background-color: transparent;
    color: #18aeff;
    border: 1px solid #18aeff;
}
</style>
